// @ts-nocheck
import * as React from "react";
import { Ic, Sidebar, Topbar, Kpi, AppFooter, useCurrentUser, apiErrorMessage, OrgCombobox } from "./common";
import { useEscapeKey } from "../components/useEscapeKey";
import { Pager } from "../components/Pager";
import { RowMenu } from "../components/RowMenu";
import { fullTimestamp } from "../components/timeFormat";
import { usePermissionsFromSession } from "../components/permissions";
import { useSaveToast } from "../components/SaveToast";

// Admin Users — 관리자
// Platform role: manage platform/region admins (invite, role, last sign-in).

const ROLE_LABEL = {
  system_admin:  { label: "시스템 관리자", color: "rose"   },
  region_admin:  { label: "권역 관리자",   color: "sky"    },
  agency:        { label: "유관기관 담당자", color: "amber" },
};
const STATE_LABEL = {
  active:    { label: "활성",     color: "green"  },
  invited:   { label: "초대 발송", color: "amber"  },
  paused:    { label: "정지",     color: "rose"   },
  off:       { label: "탈퇴",     color: "gray"   },
};

// 마지막 접속 시각을 한글 상대 시간으로. 너무 오래된 값은 yyyy-MM-dd로 떨어뜨려
// 가독성 유지. null/undefined → "—" (접속 이력 없음).
function lastSeenLabel(ts) {
  if (!ts) return "—";
  const t    = new Date(ts);
  const diff = Date.now() - t.getTime();
  const min  = Math.floor(diff / 60000);
  if (min < 0)       return t.toLocaleString("ko-KR", { dateStyle: "short", timeStyle: "short" });
  if (min < 1)       return "방금 전";
  if (min < 60)      return `${min}분 전`;
  const hour = Math.floor(min / 60);
  if (hour < 24)     return `${hour}시간 전`;
  const day = Math.floor(hour / 24);
  if (day < 7)       return `${day}일 전`;
  if (day < 30)      return `${Math.floor(day / 7)}주 전`;
  // 한 달 이상은 절대 날짜가 더 의미 있음 — "3개월 전"은 모호.
  return t.toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" })
          .replace(/\.\s?/g, "-").replace(/-$/, "");
}

// 비밀번호 만료까지 N일. 양수 = 남은 일, 0/음수 = 경과 일. null = 미설정.
// 임계: 14일 이내 = warning(amber), 만료 = critical(rose), 그 외는 muted.
function passwordExpiryLabel(days) {
  if (days == null) return null;
  if (days < 0)  return { text: `만료 ${-days}일 경과`,    tone: "rose"  };
  if (days === 0) return { text: "오늘 만료",              tone: "rose"  };
  if (days <= 14) return { text: `${days}일 후 만료`,       tone: "amber" };
  return null; // 14일 이상 남으면 표시 안 함 — 노이즈 감소
}

// 잠금 해제까지 남은 시간 라벨. 음수(이미 지남)면 빈 문자열 — locked 자체가 false일 것.
function unlockEtaLabel(ts) {
  if (!ts) return "";
  const diffMin = Math.ceil((new Date(ts).getTime() - Date.now()) / 60000);
  if (diffMin <= 0) return "";
  if (diffMin < 60) return `약 ${diffMin}분 후 자동 해제`;
  return `${Math.ceil(diffMin / 60)}시간 후 자동 해제`;
}

// 초대 시각을 "방금/N분 전/N시간 전/N일 전" 라벨로. 응답 없는 초대 식별용.
function invitedAgoLabel(ts) {
  if (!ts) return "";
  const diffMin = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
  if (diffMin < 1)    return "방금 초대";
  if (diffMin < 60)   return `초대 ${diffMin}분 전`;
  if (diffMin < 1440) return `초대 ${Math.round(diffMin / 60)}시간 전`;
  return `초대 ${Math.round(diffMin / 1440)}일 전`;
}

// 401/403 응답 시 한글 사유 메시지로 변환. 그 외엔 null(정상).
async function authGateMessage(res) {
  if (res.status === 401) return "로그인이 필요합니다.";
  if (res.status === 403) {
    const j = await res.json().catch(() => ({}));
    return j.message || "시스템 관리자 권한이 필요합니다.";
  }
  return null;
}

// 권역 미배정(전체 관리자)을 의미하는 폼 sentinel. 백엔드엔 region_id 자체를
// 보내지 않거나 null로 전송한다. 빈 문자열은 셀렉트의 native 기본값으로 안전.
const REGION_NONE = "";
// 모달 폼의 빈 초기값 — 초대 모드 진입/모달 닫기 시 이 값으로 리셋해
// 직전 편집 데이터가 초대 폼에 남는 것을 막는다.
const EMPTY_FORM = { name:"", email:"", role:"region_admin", region_id: REGION_NONE, organization_id: "" };

// 한 페이지 행 수. 너무 작으면 페이지 이동이 잦고, 너무 크면 스크롤 부담.
// 표 화면이라 카드 그리드보다 더 많이 담아도 자연스럽다.
const PER_PAGE = 20;

function Admins() {
  const { user, loaded: userLoaded } = useCurrentUser();
  const perms = usePermissionsFromSession({ user, loaded: userLoaded });
  const [admins, setAdmins] = React.useState([]);
  const [regions, setRegions] = React.useState([]);
  // 유관기관(agency) 초대·편집 시 소속 기관 선택용. minimal=1 경량 응답({id,name,
  // region_id,category})을 받아 OrgCombobox가 클라이언트에서 검색·상위 N개만 렌더한다.
  const [orgs, setOrgs] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [role, setRole] = React.useState("all");
  // 기본은 활성 계정만 — 정지/탈퇴/초대대기가 섞여 목록이 길어지는 걸 막는다.
  // "전체 상태"를 선택하면 모든 상태를 본다.
  const [state, setState] = React.useState("active");
  // 권역 필터: "all" | "none"(권역 미지정) | region_id 문자열.
  // backend의 build_scope가 같은 어휘를 사용해 URL/payload가 그대로 통과.
  const [regionFilter, setRegionFilter] = React.useState("all");
  const [open, setOpen] = React.useState(false);
  // null = invite mode, else admin id being edited.
  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const { notifySaved, saveToast } = useSaveToast();
  // 권한 거부/네트워크 실패 시 빈 표 대신 명확한 메시지를 위해.
  const [accessError, setAccessError] = React.useState(null);
  // 서버 페이지네이션: page를 서버에 보내고 envelope를 받는다.
  // total은 envelope.pagination.total — 클라이언트 admins.length가 아님(현재 페이지만).
  const [page, setPage] = React.useState(1);
  const [pagination, setPagination] = React.useState({ page: 1, total: 0, total_pages: 1 });
  // KPI 카드용 — 페이지가 아니라 필터된 전체 집계. 서버 envelope.counts에서 받음.
  const [counts, setCounts] = React.useState({ total: 0, active: 0, invited: 0 });
  useEscapeKey(() => closeModal(), open);

  // 실제 Region 테이블에서 후보를 가져와 셀렉트에 채운다. 마운트 시 1회.
  // 실패해도 "전체"만 있는 셀렉트로 폴백되어 모달이 깨지진 않는다.
  React.useEffect(() => {
    fetch("/api/v1/regions", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : [])
      .then(list => setRegions(Array.isArray(list) ? list : []))
      .catch(() => setRegions([]));
    fetch("/api/v1/organizations?minimal=1", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : [])
      .then(list => setOrgs(Array.isArray(list) ? list : []))
      .catch(() => setOrgs([]));
  }, []);

  // 검색어는 한 글자마다 요청을 낸다. 응답 순서는 보장되지 않아서, 짧은 질의
  // ("e")가 느리게 돌아오면 마지막(정확한) 질의 결과를 덮어써 목록이 필터 전으로
  // 되돌아간다 — 검색창에는 이름이 그대로 있는데 표는 전체가 보이는 상태.
  // 요청마다 일련번호를 매겨 **최신 응답만** 반영한다.
  const reqSeq = React.useRef(0);
  const load = React.useCallback(async () => {
    const seq = ++reqSeq.current;
    const stale = () => seq !== reqSeq.current;
    const params = new URLSearchParams();
    if (q)     params.set("q", q);
    if (role         !== "all") params.set("role", role);
    if (state        !== "all") params.set("state", state);
    if (regionFilter !== "all") params.set("region_id", regionFilter);
    params.set("page", String(page));
    params.set("per_page", String(PER_PAGE));
    try {
      const r = await fetch(`/api/v1/admins?${params.toString()}`, { credentials: "same-origin" });
      if (stale()) return;
      const gateMsg = await authGateMessage(r);
      if (gateMsg) { setAccessError(gateMsg); setAdmins([]); setPagination({ page:1, total:0, total_pages:1 }); return; }
      if (!r.ok) { setAccessError(apiErrorMessage(null, r.status)); setAdmins([]); return; }
      setAccessError(null);
      const data = await r.json();
      if (stale()) return;
      // page 파라미터를 보냈으니 백엔드는 envelope를 반환. 방어적으로 Array도 수용.
      if (Array.isArray(data)) {
        setAdmins(data);
        setPagination({ page: 1, total: data.length, total_pages: 1 });
        setCounts({
          total: data.length,
          active: data.filter(a => a.state === "active").length,
          invited: data.filter(a => a.state === "invited").length,
        });
      } else {
        setAdmins(data.admins || []);
        setPagination(data.pagination || { page: 1, total: 0, total_pages: 1 });
        setCounts(data.counts || { total: 0, active: 0, invited: 0 });
      }
    } catch (e) {
      if (stale()) return;
      setAccessError(apiErrorMessage(e));
    }
  }, [q, role, state, regionFilter, page]);
  React.useEffect(() => { load(); }, [load]);

  // 필터 변경 시 1페이지로 자동 복귀(필터된 결과가 적어 빈 페이지가 되는 걸 방지).
  React.useEffect(() => { setPage(1); }, [q, role, state, regionFilter]);
  // 페이지 범위 초과(필터로 결과가 줄어든 경우)도 1로 보정.
  React.useEffect(() => {
    if (page > pagination.total_pages && pagination.total_pages > 0) setPage(1);
  }, [page, pagination.total_pages]);

  // 모든 mutation 호출에서 권한 거부 + 422 validation 사유를 alert로 노출.
  // 정상 완료가 아니면 throw해서 호출자는 alert 중복 없이 흐름만 끊으면 됨.
  const guardedPatch = async (id, body) => {
    const r = await fetch(`/api/v1/admins/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
    const gateMsg = await authGateMessage(r);
    if (gateMsg) { alert(gateMsg); throw new Error(gateMsg); }
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      const msg = apiErrorMessage(j, r.status);
      alert(msg);
      throw new Error(msg);
    }
    return r;
  };

  // 표는 현재 페이지만 렌더. counts는 위에서 envelope.counts로 받았다.
  const filtered = admins;

  // CSV 다운로드 URL — 화면에 적용된 검색/필터를 그대로 querystring으로 전달.
  // page/per_page는 의도적으로 제외(필터 적용된 전체를 받기 위함, 백엔드도 cap만 적용).
  const exportUrl = React.useMemo(() => {
    const p = new URLSearchParams();
    if (q)                      p.set("q", q);
    if (role         !== "all") p.set("role", role);
    if (state        !== "all") p.set("state", state);
    if (regionFilter !== "all") p.set("region_id", regionFilter);
    const qs = p.toString();
    return qs ? `/api/v1/admins/export.csv?${qs}` : `/api/v1/admins/export.csv`;
  }, [q, role, state, regionFilter]);

  const invite = async () => {
    try {
      // region_id는 비었으면(전체) 아예 omit — backend가 nil로 처리.
      // 유관기관 담당자는 소속 기관(organization_id)을 보내고 권역은 백엔드가 파생한다.
      const payload = { name: form.name, email: form.email, role: form.role };
      if (form.role === "agency") {
        payload.organization_id = form.organization_id ? Number(form.organization_id) : null;
      } else if (form.region_id) {
        payload.region_id = Number(form.region_id);
      }
      const r = await fetch("/api/v1/admins", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });
      const gateMsg = await authGateMessage(r);
      if (gateMsg) { alert(gateMsg); return; }
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        alert(apiErrorMessage(j, r.status));
        return;
      }
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
      notifySaved("초대 이메일을 발송했습니다");
    } catch (e) { alert(apiErrorMessage(e)); }
  };
  // Persists name/email/role/state/region changes for an existing admin.
  const saveEdit = async () => {
    if (!editingId) return;
    try {
      // 이름·이메일·역할·권역·상태 모두 PATCH로 묶어 보냄.
      // 빈 region_id는 null(권역 해제)로 명시 전송.
      await guardedPatch(editingId, {
        name:        form.name,
        email:       form.email,
        role:        form.role,
        state:       form.state,
        region_id:   form.region_id === REGION_NONE ? null : Number(form.region_id),
        // 유관기관 담당자만 소속 기관을 전송(백엔드가 권역 파생). 그 외 역할은 null로
        // 명시해 소속 기관 연결을 해제한다.
        organization_id: form.role === "agency"
          ? (form.organization_id ? Number(form.organization_id) : null)
          : null,
      });
      closeModal();
      load();
      notifySaved();
    } catch { /* alert는 guardedPatch가 이미 표시 */ }
  };
  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      name: a.name, email: a.email, role: a.role,
      // 표시는 region(name), 폼은 region_id(서버에 보낼 값). 둘 다 보존.
      // 단 시스템 관리자는 권역 개념이 없으므로 레거시 값이 있어도 미지정으로 정규화.
      region_id: a.role === "system_admin" ? REGION_NONE : (a.region_id ?? REGION_NONE),
      // 유관기관 담당자는 현재 소속 기관을 OrgCombobox에 선택해둔다.
      organization_id: a.organization_id ? String(a.organization_id) : "",
      state: a.state,
    });
    setOpen(true);
  };
  // 초대 모드로 모달 열기 — editingId/form을 명시적으로 비워, 직전 편집 데이터가
  // 초대 폼에 남는 버그를 막는다.
  const openInvite = () => { setEditingId(null); setForm(EMPTY_FORM); setOpen(true); };
  // 모달 닫기 — 모든 닫기 경로(취소/X/ESC/배경)에서 form까지 리셋해 상태를 깨끗이.
  const closeModal = () => { setOpen(false); setEditingId(null); setForm(EMPTY_FORM); };
  // 정지/활성화는 로그인 가능 여부를 바꾸므로 의도치 않은 클릭 방지용 확인을 받는다.
  // a = 관리자 객체(이름/이메일 표시용), s = 목표 상태("paused" | "active").
  const setAdminState = async (a, s) => {
    const msg = s === "paused"
      ? `${a.name} (${a.email}) 계정을 정지합니다.\n정지된 관리자는 로그인할 수 없습니다. "활성화"로 언제든 해제할 수 있습니다.\n\n진행하시겠습니까?`
      : `${a.name} (${a.email}) 계정을 활성화합니다.\n로그인이 다시 허용됩니다.\n\n진행하시겠습니까?`;
    if (!confirm(msg)) return;
    try { await guardedPatch(a.id, { state: s }); load(); } catch {}
  };
  // 탈퇴 처리는 의도와 위험이 커서 확인 다이얼로그 + 명시적 경고.
  // 백엔드가 셀프/마지막 system_admin은 422로 막아 추가 안전망 있음.
  const offboardAdmin = async (a) => {
    if (!confirm(`${a.name} (${a.email}) 계정을 탈퇴 처리합니다.\n탈퇴 후엔 로그인이 차단되며, "재활성" 버튼으로만 복구 가능합니다.\n\n진행하시겠습니까?`)) return;
    try { await guardedPatch(a.id, { state: "off" }); load(); } catch {}
  };
  // 비밀번호 재설정: 새 임시 비밀번호 발급 + 메일 발송. 잠금 상태도 자동 해제.
  // 확인 alert으로 의도치 않은 클릭 방지.
  const resetPassword = async (a) => {
    if (!confirm(`${a.email} 계정의 비밀번호를 재설정합니다.\n새 임시 비밀번호가 이메일로 발송되며, 잠긴 계정은 같이 해제됩니다.`)) return;
    try {
      const r = await fetch(`/api/v1/admins/${a.id}/reset_password`, {
        method: "POST", credentials: "same-origin", headers: { Accept: "application/json" },
      });
      const gateMsg = await authGateMessage(r);
      if (gateMsg) { alert(gateMsg); return; }
      if (!r.ok) { alert(apiErrorMessage(null, r.status)); return; }
      alert(`${a.email} 주소로 새 임시 비밀번호를 발송했습니다.`);
      load();
    } catch (e) { alert(apiErrorMessage(e)); }
  };

  // 잠긴 계정 수동 해제. 멱등이라 unlock 버튼 노출 조건만 잘 잡으면 OK.
  const unlockAdmin = async (a) => {
    try {
      const r = await fetch(`/api/v1/admins/${a.id}/unlock`, {
        method: "POST", credentials: "same-origin", headers: { Accept: "application/json" },
      });
      const gateMsg = await authGateMessage(r);
      if (gateMsg) { alert(gateMsg); return; }
      if (!r.ok) { alert(apiErrorMessage(null, r.status)); return; }
      load();
    } catch (e) { alert(apiErrorMessage(e)); }
  };

  // 재초대: 새 임시 비밀번호 발급 + 메일 실제 발송 (전용 엔드포인트).
  // 백엔드가 200 응답 후에만 성공 알림을 노출 — 실제 발송 결과와 UX 일치.
  const resendInvite = async (a) => {
    try {
      const r = await fetch(`/api/v1/admins/${a.id}/resend_invite`, {
        method: "POST",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      const gateMsg = await authGateMessage(r);
      if (gateMsg) { alert(gateMsg); return; }
      if (!r.ok) { alert(apiErrorMessage(null, r.status)); return; }
      alert(`${a.email} 주소로 초대 메일을 재발송했습니다. 새 임시 비밀번호가 발급되었습니다.`);
      load();
    } catch (e) { alert(apiErrorMessage(e)); }
  };

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="platform" active="admin"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="관리자" sub="플랫폼·권역 관리자 계정과 권한"/>
          <div className="main">

            <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:18}}>
              <div>
                <div className="h1">관리자 계정</div>
                <div className="muted" style={{marginTop:4, fontSize:13}}>시스템 관리자가 권역 관리자/운영자를 초대하고 권한을 부여합니다.</div>
              </div>
              <div style={{display:'flex', gap:8}}>
                {perms.canManageAdmins && (
                  <button className="btn btn-primary btn-md" onClick={openInvite}>{Ic.plus} 관리자 초대</button>
                )}
              </div>
            </div>

            {accessError && (
              <div style={{padding:'12px 16px', background:'var(--rose-50)', color:'var(--rose-500)',
                           borderRadius:10, marginBottom:14, fontSize:13, display:'flex', alignItems:'center', gap:10}}>
                <span style={{fontWeight:700}}>접근 불가</span>
                <span>{accessError}</span>
                <a href="/login" className="btn btn-ghost btn-sm" style={{marginLeft:'auto'}}>로그인</a>
              </div>
            )}

            <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:18}}>
              <Kpi label="관리자 총원"   value={`${counts.total.toLocaleString()}명`}/>
              <Kpi label="활성 계정"     value={`${counts.active.toLocaleString()}명`}  color="var(--green-500)"/>
              <Kpi label="초대 대기"     value={`${counts.invited.toLocaleString()}명`} color="var(--amber-500)"/>
            </div>

            <div className="card" style={{overflow:'hidden'}}>
              <div style={{padding:'14px 22px', display:'flex', alignItems:'center', gap:10, borderBottom:'1px solid var(--border-soft)'}}>
                <div className="searchbar" style={{flex:'0 0 260px'}}>{Ic.search}<input className="input" placeholder="이름·이메일·권역" value={q} onChange={e=>setQ(e.target.value)}/></div>
                <select className="select" value={role} onChange={e=>setRole(e.target.value)} style={{width:140}}>
                  <option value="all">전체 역할</option>
                  <option value="system_admin">시스템 관리자</option>
                  <option value="region_admin">권역 관리자</option>
                  <option value="agency">유관기관 담당자</option>
                </select>
                <select className="select" value={state} onChange={e=>setState(e.target.value)} style={{width:120}}>
                  <option value="all">전체 상태</option>
                  <option value="active">활성</option>
                  <option value="invited">초대</option>
                  <option value="paused">정지</option>
                  <option value="off">탈퇴</option>
                </select>
                <select className="select" value={regionFilter} onChange={e=>setRegionFilter(e.target.value)} style={{width:160}}>
                  <option value="all">전체 권역</option>
                  <option value="none">권역 미지정</option>
                  {regions.map(r => (
                    <option key={r.id} value={r.id}>{r.code} · {r.name}</option>
                  ))}
                </select>
                <div style={{flex:1}}/>
                <a className="btn btn-ghost btn-sm"
                  href={exportUrl}
                  style={{textDecoration:'none'}}
                  title="현재 필터 적용된 관리자 목록을 CSV로 내려받기 (감사 로그에 기록됨)">
                  {Ic.arrow} CSV 내보내기
                </a>
              </div>
              <table className="tbl">
                <thead><tr>
                  <th style={{paddingLeft:22}}>관리자</th>
                  <th>역할</th>
                  <th>담당 권역</th>
                  <th>담당 기관</th>
                  <th>마지막 접속</th>
                  <th>IP</th>
                  <th>상태</th>
                  <th style={{textAlign:'right', paddingRight:22}}>작업</th>
                </tr></thead>
                <tbody>
                  {filtered.map(a => {
                    const rb = ROLE_LABEL[a.role] || { label: a.role, color: "gray" };
                    const sb = STATE_LABEL[a.state] || { label: a.state, color: "gray" };
                    return (
                      <tr key={a.id}>
                        <td style={{paddingLeft:22}}>
                          <div style={{display:'flex', alignItems:'center', gap:10}}>
                            <div className="avatar sm" style={{background:'linear-gradient(135deg,#EFB287,#E8825B)'}}>{a.name[0]}</div>
                            <div>
                              <div style={{fontWeight:700, fontSize:12.5}}>{a.name}</div>
                              <div className="muted" style={{fontSize:10.5}}>{a.email}</div>
                            </div>
                          </div>
                        </td>
                        <td><span className={"badge badge-" + rb.color}>{rb.label}</span></td>
                        <td className="muted">{a.region}</td>
                        {/* 담당 기관 — 유관기관 담당자만 소속 기관명, 그 외 역할은 해당 없음 */}
                        <td className="muted">{a.organization_name || "—"}</td>
                        <td className="mono muted">
                          <span title={fullTimestamp(a.last)}>{lastSeenLabel(a.last)}</span>
                          {(() => {
                            // 만료 임박/경과일 때만 별도 라인. 평소엔 표 가독성 우선.
                            const e = passwordExpiryLabel(a.password_expires_in_days);
                            if (!e) return null;
                            const color = e.tone === "rose" ? "var(--rose-500)" : "var(--amber-500)";
                            return (
                              <div style={{fontSize:10, marginTop:3, color, fontWeight:700}}
                                title={`비밀번호 마지막 변경: ${fullTimestamp(a.password_changed_at)}`}>
                                🔑 {e.text}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="mono muted">{a.ip}</td>
                        <td>
                          {a.locked ? (
                            <span className="badge badge-rose" title={`로그인 ${a.failed_login_attempts}회 실패로 잠김`}>🔒 잠김</span>
                          ) : (
                            <span className={"badge badge-" + sb.color}>{sb.label}</span>
                          )}
                          {a.locked && a.locked_until && (
                            <div className="muted" style={{fontSize:10, marginTop:3}}
                              title={`자동 해제: ${fullTimestamp(a.locked_until)}`}>
                              {unlockEtaLabel(a.locked_until)}
                            </div>
                          )}
                          {a.state === "invited" && a.invited_at && !a.locked && (
                            <div className="muted" style={{fontSize:10, marginTop:3}}
                              title={`초대 발송: ${fullTimestamp(a.invited_at)}`}>
                              {invitedAgoLabel(a.invited_at)}
                            </div>
                          )}
                        </td>
                        <td style={{textAlign:'right', paddingRight:22}}>
                          {(() => {
                            // 자주 쓰는 1차 액션(편집)만 인라인으로 노출하고, 나머지 보조·
                            // 위험 액션은 ⋯ 메뉴로 접어 작업 컬럼 폭을 일정하게 유지한다.
                            const e = passwordExpiryLabel(a.password_expires_in_days);
                            const pwColor = e ? (e.tone === "rose" ? 'var(--rose-500)' : 'var(--amber-600)') : undefined;
                            const menu = [
                              // 잠긴 계정은 긴급도가 높아 메뉴 최상단에 강조.
                              a.locked && { label: "잠금 해제", danger: true, onClick: () => unlockAdmin(a),
                                title: "실패 카운터 0으로 리셋 + 잠금 해제" },
                              { label: e ? `비번 재설정 · ${e.text}` : "비번 재설정", color: pwColor,
                                onClick: () => resetPassword(a), title: "새 임시 비밀번호 발급 + 메일 발송" },
                              perms.canManageAdmins && a.state === 'active'  && { label: "정지", onClick: () => setAdminState(a, 'paused') },
                              perms.canManageAdmins && a.state === 'paused'  && { label: "활성화", onClick: () => setAdminState(a, 'active') },
                              perms.canManageAdmins && a.state === 'invited' && { label: "초대 재전송", onClick: () => resendInvite(a) },
                              perms.canManageAdmins && (a.state === 'active' || a.state === 'paused') && {
                                label: "탈퇴", danger: true, dividerBefore: true, onClick: () => offboardAdmin(a),
                                title: "계정을 탈퇴 처리 (로그인 차단)" },
                              perms.canManageAdmins && a.state === 'off' && {
                                label: "재활성", color: 'var(--green-600)', dividerBefore: true,
                                onClick: () => setAdminState(a, 'active'), title: "탈퇴 계정 다시 활성화" },
                            ];
                            return (
                              <div style={{display:'inline-flex', alignItems:'center', gap:4, justifyContent:'flex-end'}}>
                                {perms.canManageAdmins && (
                                  <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(a)}>편집</button>
                                )}
                                <RowMenu items={menu} />
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {pagination.total > 0 && (
                <div style={{padding:'10px 22px', borderTop:'1px solid var(--border-soft)',
                             display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                  <span className="muted" style={{fontSize:11.5}}>
                    페이지 <b>{pagination.page}</b> / {pagination.total_pages} ·
                    전체 <b>{pagination.total}</b>명 (페이지당 {PER_PAGE}명)
                  </span>
                  <Pager page={pagination.page} totalPages={pagination.total_pages} onPage={setPage}/>
                </div>
              )}
            </div>

            <div className="card" style={{marginTop:14, padding:18}}>
              <div className="h3">권한 정책</div>
              <div className="muted" style={{fontSize:12, marginTop:4}}>역할별로 부여되는 화면·기능 접근 권한</div>
              <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginTop:14}}>
                {Object.entries(ROLE_LABEL).map(([k, v]) => (
                  <div key={k} className="card card-pad" style={{padding:14}}>
                    <span className={"badge badge-" + v.color}>{v.label}</span>
                    <ul style={{fontSize:11.5, color:'var(--ink-2)', marginTop:10, paddingLeft:14, lineHeight:1.7}}>
                      {k === 'system_admin' && <>
                        <li>전체 권역 · 모든 모듈</li>
                        <li>관리자 초대 · 권한 부여</li>
                        <li>시스템 설정 · 감사 로그</li>
                      </>}
                      {k === 'region_admin' && <>
                        <li>담당 권역 전체</li>
                        <li>케이스·매니저·기관</li>
                        <li>리포트 생성</li>
                      </>}
                      {k === 'agency' && <>
                        <li>소속 유관기관 정보 관리</li>
                        <li>기관 지도·디렉터리 열람</li>
                      </>}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

          </div>
          <AppFooter/>
        </div>
      </div>

      {open && (
        <div onClick={closeModal} style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:50}}>
          <div onClick={e=>e.stopPropagation()} className="card" style={{width:520, padding:0}}>
            <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <div>
                <div className="h3">{editingId ? "관리자 편집" : "관리자 초대"}</div>
                {editingId && <div className="muted" style={{fontSize:11, marginTop:2}}>역할 변경은 감사 로그에 기록됩니다. 자기 자신의 시스템 관리자 권한은 해제할 수 없습니다.</div>}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={closeModal}>{Ic.close}</button>
            </div>
            <div style={{padding:22, display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
              <div>
                <div className="label">이름</div>
                <input className="input" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} placeholder="홍길동"/>
              </div>
              <div>
                <div className="label">이메일</div>
                <input className="input" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} placeholder="name@care.local"/>
              </div>
              <div>
                <div className="label">역할</div>
                <select className="select" value={form.role} onChange={e=>{
                  const role = e.target.value;
                  // 시스템 관리자는 전 권역 권한 → 담당 권역 개념이 없으므로 강제로 미지정.
                  // 유관기관 담당자가 아니게 되면 소속 기관 선택을 비운다(권역/시스템
                  // 관리자는 기관 개념이 없음). agency 유지 시 선택된 기관은 보존.
                  setForm({...form, role,
                    region_id: role === "system_admin" ? REGION_NONE : form.region_id,
                    organization_id: role === "agency" ? form.organization_id : "",
                  });
                }}>
                  <option value="region_admin">권역 관리자</option>
                  <option value="system_admin">시스템 관리자</option>
                  <option value="agency">유관기관 담당자</option>
                </select>
              </div>
              {form.role === "agency" ? (
                <div style={{gridColumn:'1/-1'}}>
                  <div className="label">소속 기관</div>
                  {/* 유관기관 담당자는 권역이 아니라 소속 기관에 연결된다. 기관은 병원·
                      보건소·복지시설 등 전국 수만 건이라 native <select>는 무거우므로
                      검색형 OrgCombobox로 좁혀 고른다(회원가입 흐름과 동일). 담당 권역은
                      선택한 기관에서 자동 파생한다. */}
                  <OrgCombobox
                    orgs={orgs}
                    value={form.organization_id ? Number(form.organization_id) : null}
                    onChange={id => {
                      const org = id != null ? orgs.find(o => o.id === id) : null;
                      setForm({...form,
                        organization_id: id != null ? String(id) : "",
                        region_id: org && org.region_id != null ? String(org.region_id) : REGION_NONE,
                      });
                    }}
                    placeholder="기관 이름 검색 (필수)"
                    width="100%"
                    invalid={!form.organization_id}
                  />
                  <div className="muted" style={{fontSize:11, marginTop:4}}>
                    담당 권역은 소속 기관에서 자동 설정됩니다{form.region_id ? ` · ${regions.find(r => String(r.id) === String(form.region_id))?.name || ""}` : ""}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="label">담당 권역</div>
                  <select className="select"
                    disabled={form.role === "system_admin"}
                    value={form.role === "system_admin" ? REGION_NONE : (form.region_id ?? REGION_NONE)}
                    onChange={e=>setForm({...form, region_id: e.target.value})}>
                    <option value={REGION_NONE}>{form.role === "system_admin" ? "— 전체 (전 권역) —" : "— 미지정 —"}</option>
                    {regions.map(r => (
                      <option key={r.id} value={r.id}>{r.code} · {r.name}</option>
                    ))}
                  </select>
                  {form.role === "system_admin" && (
                    <div className="muted" style={{fontSize:11, marginTop:4}}>시스템 관리자는 전 권역 권한이라 담당 권역을 지정하지 않습니다.</div>
                  )}
                </div>
              )}
              {editingId && (
                <div>
                  <div className="label">상태</div>
                  <select className="select" value={form.state || "active"} onChange={e=>setForm({...form, state:e.target.value})}>
                    <option value="active">활성</option>
                    <option value="paused">정지</option>
                    <option value="invited">초대</option>
                    <option value="off">탈퇴</option>
                  </select>
                </div>
              )}
            </div>
            <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'flex-end', gap:8}}>
              <button className="btn btn-ghost btn-md" onClick={closeModal}>취소</button>
              {editingId ? (
                <button className="btn btn-primary btn-md" onClick={saveEdit}>저장</button>
              ) : (
                <button className="btn btn-primary btn-md" onClick={invite} disabled={!form.name || !form.email || (form.role === "agency" && !form.organization_id)}>초대 이메일 발송</button>
              )}
            </div>
          </div>
        </div>
      )}
      {saveToast}
    </div>
  );
}

export { Admins };
